import { useEffect, useMemo, useRef, useState } from "react";

import {
  useOrders,
  useOrdersCache,
  useRouteAutoAssign,
  useRouteMutation,
} from "@web-app/api";
import {
  AccordionV2,
  Button,
  ConfirmModal,
  DateInput,
  Field,
  Header,
  Icon,
  Input,
  Label,
  SearchWithMultiSelect,
  SelectAssignee,
  Tooltip,
  useAlert,
} from "@web-app/components";
import useDispatchStore from "@web-app/store/dispatchStore";
import { Order, Route } from "@web-app/types";
import { getApiErrorMessage } from "@web-app/utils/errorHandler";
import { pluralize } from "@web-app/utils/helpers";

import { OrderList } from "./components/OrderList";
import { RouteValidationErrors } from "./components/RouteValidationErrors";
import { useDelayedOrderDetection } from "./hooks/useDelayedOrderDetection";
import { useEarlyDeliveryDetection } from "./hooks/useEarlyDeliveryDetection";
import { useEstimatedPickupTime } from "./hooks/useEstimatedPickupTime";
import { useInvalidTimestampDetection } from "./hooks/useInvalidTimestampDetection";
import { useRouteDrawing } from "./hooks/useRouteDrawing";
import { useRouteValidation } from "./hooks/useRouteValidation";

export default function EditRoute({
  setIsEditing,
  route,
}: {
  route: {
    details: Route | null;
    orders: Order[];
  } | null;
  setIsEditing: (isEditing: boolean) => void;
}) {
  const [estimatedPickupTime, setEstimatedPickupTime] = useState<Date | null>(
    null,
  );
  const [name, setName] = useState(route?.details?.externalId);
  const [assigneeId, setAssigneeId] = useState(
    route?.details?.assigneeId ?? null,
  );
  const [isFlexible, setIsFlexible] = useState(
    route?.details?.isFlexible ?? false,
  );
  const [showHasEarlyDeliveryOrdersModal, setShowHasEarlyDeliveryOrdersModal] =
    useState(false);
  const [isOptimizeClicked, setIsOptimizeClicked] = useState(false);
  const [respectTimeWindows, setRespectTimeWindows] = useState(false);
  const [asEarlyAsPossible, setAsEarlyAsPossible] = useState(true);
  const [earlyThresholdMinutes, setEarlyThresholdMinutes] = useState(30);
  const { showAlertAsync } = useAlert();
  const [isOverwriteAssignee, setIsOverwriteAssignee] = useState(false);
  const [showReorderWarning, setShowReorderWarning] = useState(false);
  const { updateRouteV2 } = useRouteMutation();
  const { autoAssignRoute } = useRouteAutoAssign();
  const { user: currentUser, editRouteOrderIds } = useDispatchStore();
  const { data } = useOrders();
  const orders = data?.orders;
  const assignees = data?.assignees;
  const ordersCache = useOrdersCache();

  const {
    getAssigneeValidationState,
    isAssigneesValid,
    isPickupTimeValid,
    isRouteSequenceValid,
    isWorkspaceValid,
    isSameDateOrders,
  } = useRouteValidation(editRouteOrderIds, orders, estimatedPickupTime);

  const computedEstimatedPickupTime = useEstimatedPickupTime({
    isRouteSequenceValid: isRouteSequenceValid().isValid,
    orderIds: editRouteOrderIds,
    orders,
    routeEstimatedPickupTime: route?.details?.estimatedPickupTime,
  });

  const {
    computedRouteData,
    distance,
    durations,
    isOptimizing,
    drawSimpleRoute,
    drawDirections,
    optimizeOrders,
    currentPolylineRef,
    routedOrdersDraftEstimatedETA,
    setRoutedOrdersDraftEstimatedETA,
  } = useRouteDrawing(
    editRouteOrderIds,
    orders,
    currentUser,
    assigneeId,
    name || "",
    isFlexible,
    respectTimeWindows,
    !asEarlyAsPossible,
    earlyThresholdMinutes,
    estimatedPickupTime,
  );

  const { hasPotentialDelayedOrders } = useDelayedOrderDetection({
    orders,
    routedOrdersDraftEstimatedETA,
  });

  const { invalidTimestampOrders } = useInvalidTimestampDetection({
    orderIds: editRouteOrderIds,
    orders,
  });

  const { hasPotentialEarlyDeliveryOrders } = useEarlyDeliveryDetection({
    orderIds: editRouteOrderIds,
    orders,
    routedOrdersDraftEstimatedETA,
  });

  const hasInvalidTimestamps =
    invalidTimestampOrders && invalidTimestampOrders.length > 0;

  useEffect(() => {
    const newEditRouteOrderIds: string[] = [];
    if (orders) {
      route?.orders.forEach((o) => {
        if (orders.get(o.id)) {
          newEditRouteOrderIds.push(o.id);
        }
      });
    }
    useDispatchStore.setState({
      editRouteAssigneeId: route?.details?.assigneeId ?? null,
      editRouteOrderIds: newEditRouteOrderIds,
    });
    drawSimpleRoute(newEditRouteOrderIds);

    return () => {
      if (currentPolylineRef.current) {
        currentPolylineRef.current.setMap(null);
        currentPolylineRef.current = null;
      }

      useDispatchStore.setState({
        editRouteAssigneeId: null,
        editRouteOrderIds: [],
      });
    };
  }, []);

  const prevEditRouteOrderIdsLengthRef = useRef(editRouteOrderIds.length);

  useEffect(() => {
    // Check if this is a bulk change (rectangle selection)
    const lengthDiff = Math.abs(
      editRouteOrderIds.length - prevEditRouteOrderIdsLengthRef.current,
    );
    const isBulkChange = lengthDiff > 1; // More than 1 order changed at once suggests rectangle selection

    // Don't fit bounds if this is a bulk change (rectangle selection)
    drawSimpleRoute(editRouteOrderIds, !isBulkChange);

    prevEditRouteOrderIdsLengthRef.current = editRouteOrderIds.length;
  }, [editRouteOrderIds.length]);

  // Update estimatedPickupTime when the first order changes
  useEffect(() => {
    setEstimatedPickupTime(computedEstimatedPickupTime);
  }, [computedEstimatedPickupTime]);

  const ordersArray = useMemo(() => {
    if (!orders?.size) {
      return [];
    }

    const allOrders = Array.from(orders.values());
    const filteredOrders = allOrders.filter((order) => {
      const isUnrouted =
        (!order.routeId && order.type !== "INDIVIDUAL_PICKUP_AND_DROPOFF") ||
        order.routeId === route?.details?.id;

      return isUnrouted;
    });

    return filteredOrders;
  }, [orders, route]);

  const moveOrder = (dragIndex: number, hoverIndex: number) => {
    const dragOrder = editRouteOrderIds[dragIndex];
    const newOrders = [...editRouteOrderIds];
    newOrders.splice(dragIndex, 1);
    newOrders.splice(hoverIndex, 0, dragOrder);
    useDispatchStore.setState({ editRouteOrderIds: newOrders });
    drawSimpleRoute(newOrders);
    setShowReorderWarning(true);
  };

  const removeOrder = (orderId: string) => {
    const newOrders = editRouteOrderIds.filter((oid) => oid !== orderId);
    useDispatchStore.setState({ editRouteOrderIds: newOrders });
    drawSimpleRoute(newOrders);
  };

  const updateRoute = async () => {
    if (!route?.details) return;

    if (showHasEarlyDeliveryOrdersModal) {
      setShowHasEarlyDeliveryOrdersModal(false);
    }

    const estimatedPickupDateTime = estimatedPickupTime
      ? estimatedPickupTime.toISOString()
      : null;

    await showAlertAsync(
      updateRouteV2.mutateAsync({
        assigneeId: assigneeId ?? "",
        computedData: computedRouteData ?? undefined,
        earlyThresholdMinutes: earlyThresholdMinutes,
        estimatedPickupTime: estimatedPickupDateTime,
        externalId: name,
        isFlexible: isFlexible,
        isOptimizeClicked: isOptimizeClicked,
        ordersOrder: editRouteOrderIds.map((orderId, index) => ({
          id: orderId,
          index,
          type: "order",
        })),
        respectTimeWindows: respectTimeWindows,
        routeId: route.details.id,
        shouldOptimize: false,
        useEarlyThreshold: !asEarlyAsPossible,
      }),
      {
        error: {
          message: "Unable to update route. Please try again",
          title: "Failed!",
        },
        loading: {
          message: "Updating route...",
          title: "Updating",
        },
        success: {
          message: "Successfully updated route!",
          title: "Success!",
        },
      },
    ).then(() => {
      useDispatchStore.setState({
        editRouteOrderIds: [],
      });
      ordersCache.invalidateOrdersCache();
      setIsEditing(false);
      setRoutedOrdersDraftEstimatedETA(new Map());
    });
  };

  const checkAndUpdateRoute = async () => {
    if (hasPotentialEarlyDeliveryOrders) {
      setShowHasEarlyDeliveryOrdersModal(true);
    } else {
      await updateRoute();
    }
  };

  const handleAutoAssignRoute = async () => {
    if (!route?.details) return;

    try {
      await showAlertAsync(autoAssignRoute.mutateAsync(route.details.id), {
        error: (err) => {
          const message = getApiErrorMessage(err, "Auto-assign failed");
          if (message.includes("No available drivers")) {
            return {
              message:
                "No available drivers found nearby. Try manual assignment.",
              title: "Auto Assign",
              type: "error",
            };
          } else if (message.includes("already active")) {
            return {
              message: "Auto-assign is already active for this route.",
              title: "Auto Assign",
              type: "error",
            };
          }
          return {
            message,
            title: "Auto Assign",
            type: "error",
          };
        },
        loading: {
          message: "Searching for available drivers...",
          title: "Auto Assign",
          type: "default",
        },
        success: (result) => {
          if (result.status === "QUEUED") {
            return {
              message:
                "Route queued for auto-assign. Searching for available drivers...",
              title: "Auto Assign",
              type: "success",
            };
          } else if (result.status === "SEARCHING" && result.activeOffer) {
            return {
              message: `Searching for driver — offer sent to ${result.activeOffer.driverName} (${result.activeOffer.distanceKm.toFixed(1)} km away)`,
              title: "Auto Assign",
              type: "success",
            };
          }
          setAssigneeId(result.assignedDriver!.driverId);
          useDispatchStore.setState({
            editRouteAssigneeId: result.assignedDriver!.driverId,
          });
          return {
            message: `Driver assigned — ${result.assignedDriver!.name} (${result.assignedDriver!.distanceKm.toFixed(1)} km away)`,
            title: "Auto Assign",
            type: "success",
          };
        },
      });
    } catch {
      // Error already shown via showAlertAsync
    }
  };

  if (!route) return null;
  if (!route.details) return null;

  const totalDistance = distance ? distance.toFixed(1) : "";

  return (
    <div className="absolute bg-white h-full w-full z-30 overflow-hidden flex flex-col">
      <Header
        title="Edit Route"
        leftActions={
          <Button
            icon="fasChevronLeft"
            onClick={() => setIsEditing(false)}
            plain
            className="!p-0 hover:!bg-white"
          />
        }
      />
      <div className="flex-1 overflow-y-auto px-4 mt-2 pb-4">
        <div className="flex flex-col items-center gap-2 ">
          {/* Name + Assignee row */}
          <div className="grid grid-cols-5 gap-2 w-full">
            <Field className="col-span-5 md:col-span-2">
              <Label>Name</Label>
              <Input
                type="text"
                name="externalId"
                value={name}
                onChange={(value) => setName(value)}
              />
            </Field>
            <Field className="col-span-5 md:col-span-3">
              <Label>Assignee</Label>
              <div className="grid grid-cols-8 gap-2 items-stretch">
                <div className="col-span-4 xl:col-span-5">
                  <SelectAssignee
                    selectedId={assigneeId}
                    onChange={(assigneeId) => {
                      useDispatchStore.setState({
                        editRouteAssigneeId: assigneeId,
                      });
                      setAssigneeId(assigneeId || null);
                    }}
                    dropdownClassName="md:w-[350px] lg:left-[-150px] xl:left-[-80px]"
                  />
                </div>
                {route?.details?.id && (
                  <Button
                    size="sm"
                    className="col-span-4 xl:col-span-3"
                    disabled={autoAssignRoute.isPending}
                    isLoading={autoAssignRoute.isPending}
                    onClick={handleAutoAssignRoute}
                  >
                    Auto Assign
                  </Button>
                )}
              </div>
            </Field>
          </div>

          {/* Advanced accordion */}
          <AccordionV2
            isBorderless
            noHoverBg
            noBottomBorder
            className="w-full bg-gray-100 px-2 py-3 rounded-none"
          >
            <AccordionV2.Label textAlign="left">
              <span className="flex items-center gap-2">
                <Icon name="wrench" />
                Advanced Options
              </span>
            </AccordionV2.Label>
            <AccordionV2.Content>
              <div className="flex flex-col gap-2 pt-2 pb-3">
                {estimatedPickupTime && (
                  <Field className="w-full">
                    <Label>Estimated Pickup Time</Label>
                    <DateInput
                      name="estimatedPickupTime"
                      value={estimatedPickupTime}
                      onChange={(value) => {
                        setEstimatedPickupTime(value);
                      }}
                      disabled={editRouteOrderIds.length === 0}
                      position="under"
                      timezone={orders?.get(editRouteOrderIds[0])?.timezone}
                    />
                  </Field>
                )}
                <Field className="w-full">
                  <Input
                    type="toggle"
                    name="isFlexible"
                    value={isFlexible ? "true" : "false"}
                    onChange={(value) => setIsFlexible(value === "true")}
                    rightLabel="Allow out-of-sequence delivery"
                  />
                </Field>
                <div className="w-full border-t border-gray-200 pt-2">
                  <Label>Delivery window constraints</Label>
                </div>
                <Field className="w-full">
                  <Input
                    type="toggle"
                    name="respectTimeWindows"
                    value={respectTimeWindows ? "true" : "false"}
                    onChange={(value) =>
                      setRespectTimeWindows(value === "true")
                    }
                    rightLabel="Respect delivery time windows for ETAs"
                  />
                </Field>
                {!respectTimeWindows && (
                  <>
                    <Field className="w-full">
                      <Input
                        type="toggle"
                        name="asEarlyAsPossible"
                        value={asEarlyAsPossible ? "true" : "false"}
                        onChange={(value) =>
                          setAsEarlyAsPossible(value === "true")
                        }
                        rightLabel="Arrive as early as possible (no threshold)"
                      />
                    </Field>
                    <Field className="self-start" disabled={asEarlyAsPossible}>
                      <Label disabled={asEarlyAsPossible}>
                        Early arrival threshold (minutes)
                      </Label>
                      <Input
                        type="number"
                        name="earlyThresholdMinutes"
                        value={String(earlyThresholdMinutes)}
                        onChange={(value) =>
                          setEarlyThresholdMinutes(Number(value))
                        }
                        min="0"
                        disabled={asEarlyAsPossible}
                        className="w-20"
                      />
                    </Field>
                  </>
                )}
              </div>
            </AccordionV2.Content>
          </AccordionV2>

          <Field className="w-full mt-1">
            <SearchWithMultiSelect
              hideClearButton
              showSearchIcon
              placeholder="Search orders to add to route"
              options={ordersArray.map((order) => {
                const name = order.type.includes("PICKUP")
                  ? order.pickup.name
                  : order.dropoff.name;
                const address = order.type.includes("PICKUP")
                  ? order.pickup?.address
                  : order.dropoff?.address;
                return {
                  label: `${name} - ${address || ""}`,
                  value: order.id,
                };
              })}
              selected={editRouteOrderIds}
              onChange={(orderIds: string[]) => {
                if (editRouteOrderIds.length > orderIds.length) {
                  removeOrder(
                    editRouteOrderIds.find((id) => !orderIds.includes(id))!,
                  );
                  return;
                }

                useDispatchStore.setState({
                  editRouteOrderIds: orderIds,
                });
              }}
            />
          </Field>
        </div>
        <div className="flex flex-col gap-2 mt-2">
          <div className="flex items-center justify-between gap-2">
            <Label>
              {editRouteOrderIds.length}{" "}
              {pluralize("Order", editRouteOrderIds.length)}
              {totalDistance !== "" && (
                <span className="text-sm text-gray-500 ml-2">
                  {`${totalDistance} miles`}
                </span>
              )}
            </Label>
            <div className="flex items-center gap-2">
              <Tooltip
                text={
                  editRouteOrderIds.length === 0
                    ? "Add orders to the route before computing"
                    : (!isAssigneesValid() && !isOverwriteAssignee) ||
                        !isRouteSequenceValid().isValid ||
                        hasInvalidTimestamps
                      ? "There are errors with the route. Please fix them before computing the route"
                      : "Compute the route, distance, and estimated time"
                }
                position="top"
              >
                <Button
                  icon="fasCog"
                  onClick={() => {
                    drawDirections(false);
                    setShowReorderWarning(false);
                  }}
                  plain
                  disabled={
                    editRouteOrderIds.length === 0 ||
                    (!isAssigneesValid() && !isOverwriteAssignee) ||
                    !isRouteSequenceValid().isValid ||
                    hasInvalidTimestamps ||
                    (!!estimatedPickupTime && !isPickupTimeValid().isValid)
                  }
                >
                  Compute
                </Button>
              </Tooltip>
              <Tooltip
                text={
                  editRouteOrderIds.length === 0
                    ? "Add orders to the route before optimizing"
                    : hasInvalidTimestamps ||
                        orders?.get(editRouteOrderIds[0])?.type !==
                          "ROUTE_OPT_PICKUP"
                      ? "There are errors with the route. Please fix them before optimizing the route"
                      : "Optimize orders based on optimal travel distance and time"
                }
                position="left"
              >
                <Button
                  icon="globe"
                  onClick={() => {
                    setIsOptimizeClicked(true);
                    optimizeOrders();
                    setShowReorderWarning(false);
                  }}
                  isLoading={isOptimizing}
                  disabled={
                    editRouteOrderIds.length === 0 ||
                    orders?.get(editRouteOrderIds[0])?.type !==
                      "ROUTE_OPT_PICKUP" ||
                    hasInvalidTimestamps ||
                    (!!estimatedPickupTime && !isPickupTimeValid().isValid)
                  }
                >
                  Optimize
                </Button>
              </Tooltip>
            </div>
          </div>
          <OrderList
            editRouteOrderIds={editRouteOrderIds}
            routedOrdersDraftEstimatedEta={routedOrdersDraftEstimatedETA}
            orders={orders}
            durations={durations}
            moveOrder={moveOrder}
            removeOrder={removeOrder}
          />
        </div>
      </div>
      <div className="flex-shrink-0 p-4 bg-white border-t border-gray-200 w-full">
        {editRouteOrderIds.length > 0 && (
          <RouteValidationErrors
            assigneeValidation={getAssigneeValidationState()}
            sequenceValidation={isRouteSequenceValid()}
            workspaceValidation={isWorkspaceValid()}
            isOverwriteAssignee={isOverwriteAssignee}
            setIsOverwriteAssignee={setIsOverwriteAssignee}
            dateTimezoneValidation={isSameDateOrders()}
            assigneeName={assignees?.get(assigneeId || "")?.name}
            hasPotentialDelayedOrders={hasPotentialDelayedOrders}
            hasEarlyDeliveries={hasPotentialEarlyDeliveryOrders}
            invalidTimestampOrders={invalidTimestampOrders}
            pickupTimeValidation={isPickupTimeValid()}
            showReorderWarning={showReorderWarning}
          />
        )}

        <ConfirmModal
          title={
            <div className="flex items-center gap-4">
              <Icon
                name="fasCircleExclamation"
                className="w-6 h-6 text-blue-500"
              />
              <span>Confirm route with expected early delivery</span>
            </div>
          }
          subtitle="The route will be updated with early deliveries."
          isOpen={showHasEarlyDeliveryOrdersModal}
          actions={[
            {
              label: "Confirm & Update",
              onClick: updateRoute,
              size: "sm",
            },
            {
              label: "Cancel",
              onClick: () => setShowHasEarlyDeliveryOrdersModal(false),
              plain: true,
              size: "sm",
            },
          ]}
          onClose={() => setShowHasEarlyDeliveryOrdersModal(false)}
        />
        <div className="flex gap-2">
          <Tooltip
            text={
              editRouteOrderIds.length === 0
                ? "Add orders to the route before updating"
                : (!isAssigneesValid() && !isOverwriteAssignee) ||
                    !isRouteSequenceValid().isValid ||
                    !isWorkspaceValid().isValid ||
                    hasInvalidTimestamps
                  ? "There are errors with the route. Please fix them before updating the route"
                  : undefined
            }
            position="top"
          >
            <Button
              onClick={checkAndUpdateRoute}
              disabled={
                editRouteOrderIds.length === 0 ||
                (!isAssigneesValid() && !isOverwriteAssignee) ||
                !isRouteSequenceValid().isValid ||
                !isWorkspaceValid().isValid ||
                !isSameDateOrders().isValid ||
                !isPickupTimeValid().isValid ||
                hasInvalidTimestamps ||
                updateRouteV2.isPending
              }
              isLoading={updateRouteV2.isPending}
            >
              Update
            </Button>
          </Tooltip>
          <Button
            type="button"
            plain
            onClick={() => setIsEditing(false)}
            disabled={updateRouteV2.isPending}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
